import {
  useState,
  useEffect,
  useCallback,
  useContext,
  createContext,
  createElement,
} from "react";
import { useUser, useClerk } from "@clerk/react";
import type { ReactNode } from "react";
import type { AuthUser } from "@workspace/api-client-react";

export type { AuthUser };

export type UserRole = "super_admin" | "owner" | "admin" | "analyst" | "viewer";

const ROLE_RANK: Record<UserRole, number> = {
  super_admin: 5,
  owner: 4,
  admin: 3,
  analyst: 2,
  viewer: 1,
};

/**
 * Returns true when the user's role meets or exceeds the minimum required role.
 * Users with no role default to "viewer" (lowest level).
 */
export function canAccessRole(userRole: string | null | undefined, minRole: UserRole): boolean {
  const rank = ROLE_RANK[userRole as UserRole] ?? ROLE_RANK.viewer;
  return rank >= ROLE_RANK[minRole];
}

function activeRoleKey(userId: string): string {
  return `activeRole_${userId}`;
}

interface StoredRole {
  role: string;
  /** The user's highest available role at the time the choice was saved. */
  highestRole?: string | null;
}

function readStoredRole(userId: string): StoredRole | null {
  try {
    const raw = localStorage.getItem(activeRoleKey(userId));
    if (!raw) {
      // Fall back to the legacy session-scoped value so users who already
      // picked a role in the current tab don't get bounced to the picker.
      const legacy = sessionStorage.getItem(activeRoleKey(userId));
      return legacy ? { role: legacy } : null;
    }
    if (raw.startsWith("{")) {
      const parsed = JSON.parse(raw) as StoredRole;
      if (parsed && typeof parsed.role === "string") return parsed;
      return null;
    }
    // Legacy plain-string format.
    return { role: raw };
  } catch {
    return null;
  }
}

function writeStoredRole(userId: string, value: StoredRole): void {
  try {
    localStorage.setItem(activeRoleKey(userId), JSON.stringify(value));
    sessionStorage.removeItem(activeRoleKey(userId));
  } catch {}
}

function removeStoredRole(userId: string): void {
  try {
    localStorage.removeItem(activeRoleKey(userId));
    sessionStorage.removeItem(activeRoleKey(userId));
  } catch {}
}

/**
 * Writes an active-role choice to localStorage so it persists across sessions.
 * Optionally records the user's highest available role at the time so a later
 * change can prompt them to re-pick.
 */
export function storeActiveRole(
  userId: string,
  role: string,
  highestRole?: string | null,
): void {
  writeStoredRole(userId, { role, highestRole: highestRole ?? null });
}

/**
 * Clears any stored active-role for a user.
 */
export function clearStoredActiveRole(userId: string): void {
  removeStoredRole(userId);
}

interface AuthState {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /** The role the user chose to operate as (null = use highest role). */
  activeRole: string | null;
  /** True when the user's highest available role has changed since their last visit. */
  highestRoleChanged: boolean;
  /** Persist a role choice in localStorage and update canAccess. Pass null to reset. */
  setActiveRole: (role: string | null) => void;
  login: () => void;
  logout: () => void;
  /** Returns true when the signed-in user's active (or highest) role meets `minRole`. */
  canAccess: (minRole: UserRole) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export interface AuthProviderProps {
  children: ReactNode;
}

async function fetchCurrentUser(): Promise<AuthUser | null> {
  try {
    const res = await fetch("/api/auth/user", { credentials: "include" });
    if (!res.ok) return null;
    const data = (await res.json()) as { user: AuthUser | null };
    return data.user ?? null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { isLoaded: clerkLoaded, isSignedIn } = useUser();
  const { openSignIn, signOut } = useClerk();

  const [user, setUser] = useState<AuthUser | null>(null);
  const [apiLoading, setApiLoading] = useState(true);
  const [activeRole, setActiveRoleState] = useState<string | null>(null);
  const [highestRoleChanged, setHighestRoleChanged] = useState(false);
  // Hydrate the stored active-role synchronously whenever the user identity or
  // role grants change. Using the "derived state during render" pattern (rather
  // than a useEffect) guarantees that consumers — especially route guards that
  // redirect to /select-role when activeRole is null — never observe a
  // non-null user with an un-hydrated null activeRole on the same commit.
  // The signature is composed of primitive fields only so two `user` objects
  // with identical role data produce the same string and skip rehydration.
  const userRoleSig = user
    ? `${user.id}|${user.role ?? ""}|${(user.roles ?? []).join(",")}`
    : null;
  const [hydratedSig, setHydratedSig] = useState<string | null>(null);
  if (userRoleSig !== hydratedSig) {
    // Compute the next state values first, then commit them via setters that
    // only fire when the value actually differs. `setHydratedSig(userRoleSig)`
    // alone is enough to break the loop (subsequent renders see equal sigs and
    // bail), but the explicit equality guards below add belt-and-braces
    // protection against any future regression where `user` might churn refs.
    let nextActiveRole: string | null = null;
    let nextHighestRoleChanged = false;
    if (user) {
      const stored = readStoredRole(user.id);
      const availableRoles = user.roles ?? [];
      if (!stored) {
        nextActiveRole = null;
      } else if (
        availableRoles.length > 0 &&
        !availableRoles.includes(stored.role)
      ) {
        removeStoredRole(user.id);
        nextActiveRole = null;
      } else if (
        stored.highestRole != null &&
        user.role != null &&
        stored.highestRole !== user.role
      ) {
        removeStoredRole(user.id);
        nextActiveRole = null;
        nextHighestRoleChanged = true;
      } else {
        nextActiveRole = stored.role;
      }
    }
    setHydratedSig(userRoleSig);
    if (nextActiveRole !== activeRole) setActiveRoleState(nextActiveRole);
    if (nextHighestRoleChanged !== highestRoleChanged) {
      setHighestRoleChanged(nextHighestRoleChanged);
    }
  }

  useEffect(() => {
    if (!clerkLoaded) return;

    if (!isSignedIn) {
      if (import.meta.env.DEV) {
        try {
          const didLogOut = sessionStorage.getItem("klairo_logged_out") === "1";
          if (didLogOut) {
            setUser(null);
            setApiLoading(false);
            return;
          }
        } catch {}
        fetchCurrentUser().then((u) => {
          setUser(u);
          setApiLoading(false);
        });
      } else {
        setUser(null);
        setApiLoading(false);
      }
      return;
    }

    try {
      sessionStorage.removeItem("klairo_logged_out");
    } catch {}

    setApiLoading(true);
    fetchCurrentUser().then((u) => {
      setUser(u);
      setApiLoading(false);

      if (u) {
        try {
          const inviteToken = sessionStorage.getItem("klairo_invite_token");
          if (inviteToken) {
            sessionStorage.removeItem("klairo_invite_token");
            fetch("/api/auth/consume-invite-token", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({ inviteToken }),
            }).catch(() => {});
          }
        } catch {}
      }
    });
  }, [clerkLoaded, isSignedIn]);

  const setActiveRole = useCallback(
    (role: string | null) => {
      setActiveRoleState(role);
      setHighestRoleChanged(false);
      if (user) {
        if (role === null) {
          removeStoredRole(user.id);
        } else {
          writeStoredRole(user.id, { role, highestRole: user.role ?? null });
        }
      }
    },
    [user],
  );

  const login = useCallback(() => {
    openSignIn({ fallbackRedirectUrl: window.location.href });
  }, [openSignIn]);

  const logout = useCallback(() => {
    try {
      sessionStorage.setItem("klairo_logged_out", "1");
    } catch {}
    signOut().then(() => {
      const base = import.meta.env.BASE_URL ?? "";
      window.location.href = base || "/";
    });
  }, [signOut]);

  const canAccess = useCallback(
    (minRole: UserRole): boolean => {
      const effectiveRole = activeRole ?? user?.role;
      return canAccessRole(effectiveRole, minRole);
    },
    [user, activeRole],
  );

  const value: AuthState = {
    user,
    isLoading: !clerkLoaded || apiLoading,
    isAuthenticated: !!user,
    activeRole,
    highestRoleChanged,
    setActiveRole,
    login,
    logout,
    canAccess,
  };

  return createElement(AuthContext.Provider, { value }, children);
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}
